import React from "react";
import { useTranslation } from "react-i18next";
import TimeService from "src/services/timeService/timeService";
import { useDispatch, useSelector } from "src/store";

import { closeDetails, selectDetailsRollout } from "@dashboard/devices/store";
import { useGetRolloutByIdQuery } from "@dashboard/rollouts/api";
import { rolloutStatusLabel } from "@dashboard/rollouts/utils/rolloutStatus";
import { Box, Typography, useTheme } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { ProgressBar } from "@shared/ProgressBar";

import { ClosePanelButton } from "../shared/ClosePanelButton";
import { PaneElement } from "../shared/PaneElement";
import { PropertyKey, PropertyRow, PropertyValue } from "../shared/Property";
import { useStyle } from "../shared/style";

import { RolloutDevicesTable } from "./RolloutDevicesTable";

export const RolloutSidePanel = () => {
    const { t } = useTranslation(["sidepanels", "common"]);
    const dispatch = useDispatch();
    const { classes } = useStyle();
    const theme = useTheme();

    const details = useSelector(selectDetailsRollout);
    const { data, isFetching } = useGetRolloutByIdQuery(details ? { id: details.selectedId } : skipToken);
    const rollout = data?.model ?? null;

    if (!details) {
        return null;
    }

    const schedule =
        rollout?.installWindowStart && rollout?.installWindowEnd
            ? t("sidepanels:rolloutPanel.confirm.scheduleTimeWindow", {
                  start: TimeService.getLocalDate(rollout.installWindowStart, "ISO", "dateTime"),
                  end: TimeService.getLocalDate(rollout.installWindowEnd, "ISO", "dateTime"),
              })
            : t("sidepanels:rolloutPanel.confirm.scheduleWhenFound");

    return (
        <React.Fragment>
            <PaneElement.HeaderBox>
                <Typography variant="h6" className={classes.panelTitle}>
                    {t("sidepanels:rolloutDetailsPanel.title")}
                </Typography>
                <Box className={classes.buttonBox}>
                    <ClosePanelButton onClick={() => dispatch(closeDetails())} />
                </Box>
            </PaneElement.HeaderBox>

            <PaneElement.Container>
                {isFetching && !rollout ? (
                    <PaneElement.Loader />
                ) : !rollout ? (
                    <PaneElement.NoData message={t("common:error.rollouts.failedToFetchRollout")} />
                ) : (
                    <React.Fragment>
                        <PaneElement.Section sx={{ flexShrink: 0 }}>
                            <PaneElement.Header>{t("common:label.general")}</PaneElement.Header>
                            <PropertyRow>
                                <PropertyKey>{t("common:label.status")}</PropertyKey>
                                <PropertyValue>{rolloutStatusLabel[rollout.status]?.() ?? rollout.status}</PropertyValue>
                            </PropertyRow>
                            <PropertyRow>
                                <PropertyKey>{t("common:label.progress")}</PropertyKey>
                                <PropertyValue>
                                    <ProgressBar progress={rollout.progress} color={theme.palette.emteria.action} />
                                </PropertyValue>
                            </PropertyRow>
                            <PropertyRow>
                                <PropertyKey>{t("common:label.version")}</PropertyKey>
                                <PropertyValue>{rollout.version}</PropertyValue>
                            </PropertyRow>
                            <PropertyRow>
                                <PropertyKey>{t("common:label.product")}</PropertyKey>
                                <PropertyValue>{rollout.codename}</PropertyValue>
                            </PropertyRow>
                            <PropertyRow>
                                <PropertyKey>{t("common:label.devices")}</PropertyKey>
                                <PropertyValue>
                                    {t("sidepanels:rolloutPanel.confirm.devicesCount", { count: rollout.deviceCount })}
                                </PropertyValue>
                            </PropertyRow>
                            <PropertyRow>
                                <PropertyKey>{t("sidepanels:rolloutDetailsPanel.properties.schedule")}</PropertyKey>
                                <PropertyValue>{schedule}</PropertyValue>
                            </PropertyRow>
                            <PropertyRow>
                                <PropertyKey>{t("common:label.created")}</PropertyKey>
                                <PropertyValue>
                                    {rollout.created ? TimeService.getLocalDate(rollout.created, "ISO", "dateTime") : "-"}
                                </PropertyValue>
                            </PropertyRow>
                            {rollout.cancelled ? (
                                <PropertyRow>
                                    <PropertyKey>{t("common:status.cancelled")}</PropertyKey>
                                    <PropertyValue>
                                        {TimeService.getLocalDate(rollout.cancelled, "ISO", "dateTime")}
                                    </PropertyValue>
                                </PropertyRow>
                            ) : null}
                        </PaneElement.Section>
                        <RolloutDevicesTable key={rollout.id} rolloutId={rollout.id} />
                    </React.Fragment>
                )}
            </PaneElement.Container>
        </React.Fragment>
    );
};
